import { apiEndpoint, getZaplifySdk } from '@zaplify/sdk';
import { importProspectsActionTypes } from '../../actionTypes';
import { getFormattedImportProspects } from './functions/importProspectsFunctions';
import { IPublicClientApplication } from '@azure/msal-browser';
import { BulkImportProspectDataDto, BulkImportValidationResponseDto, CreateProspectDataDto } from '@zaplify/prospects';
import { chunkArray } from '@zaplify/utils';

export const updateImportProspectsStep = (step) => {
    return {
        type: importProspectsActionTypes.UPDATE_STEP,
        step: step,
    };
};

export const addProspectsToImportProspects = (prospects) => {
    return {
        type: importProspectsActionTypes.UPDATE_PROSPECTS,
        prospects: prospects,
    };
};

export const updateImportProspectsReviewDetails = (reviewDetails) => {
    return {
        type: importProspectsActionTypes.UPDATE_REVIEW_DETAILS,
        reviewDetails: reviewDetails,
    };
};

export const updateAvailableImportProspectsFields = (fields) => {
    return {
        type: importProspectsActionTypes.UPDATE_AVAILABLE_FIELDS,
        fields: fields,
    };
};

export const updateProspectsFieldsInImportProspects = (fields) => {
    return {
        type: importProspectsActionTypes.UPDATE_IMPORTED_PROSPECTS_FIELDS,
        fields: fields,
    };
};

export const updateImportProspectsField = (value, field, idx) => {
    return {
        type: importProspectsActionTypes.UPDATE_FIELD,
        payload: {
            value: value,
            field: field,
            idx: idx,
        },
    };
};

export const addImportProspectsField = () => {
    return {
        type: importProspectsActionTypes.ADD_FIELD,
    };
};

export const deleteImportProspectsField = (index) => {
    return {
        type: importProspectsActionTypes.DELETE_FIELD,
        index: index,
    };
};

export const resetImportProspects = () => {
    return {
        type: importProspectsActionTypes.RESET_IMPORT_PROSPECTS,
    };
};

export const updateWholeImportProspectsState = (payload) => {
    return {
        type: importProspectsActionTypes.UPDATE_WHOLE_STATE,
        payload: payload,
    };
};

// export const createBulkImport =
//     () =>
//     async (dispatch, getState, { getFirebase }) => {
//         const profilesSdk = getZaplifySdk().profiles;
//         const campaignName = getState().newCampaign.name;
//         try {
//             const createBulkImportResponse = await profilesSdk.sources.createBulkImport({
//                 bulkImportConfigDto: {
//                     excludeInOrganization: true,
//                 },
//                 name: campaignName,
//             });

//             dispatch(updateValue('sources', [createBulkImportResponse.id]));
//             return createBulkImportResponse.id;
//         } catch (error) {
//             console.log('error creating bulk import');
//         } finally {
//             //dispatch(stopLoading());
//         }
//     };

export const validateImportedProspects = (msalInstance: IPublicClientApplication) => async (dispatch, getState) => {
    const profilesSdk = getZaplifySdk(apiEndpoint, msalInstance).profiles;
    const prospects = getState().importProspects.prospects;
    const fieldMapping = getState().importProspects.fieldMapping;
    try {
        const formattedProspects = getFormattedImportProspects(prospects, fieldMapping);

        const aggregatedResponse: BulkImportValidationResponseDto & { selectedProspects: BulkImportProspectDataDto[] } =
            {
                selectedProspects: [],
                validProspects: [],
                alreadyExistingProspects: [],
                errorProspects: [],
                prospectAlternatives: [],
                skippedProspects: [],
            };

        for (const prospectBatch of chunkArray(formattedProspects, 500)) {
            //sending validate prospects api request
            const validationResponse = await profilesSdk.sources.validateBulkImport({
                prospectsData: prospectBatch as any[],
            });
            const responseWithSelected = {
                ...validationResponse,
                selectedProspects: validationResponse.validProspects,
            };

            for (const validProspect of responseWithSelected.validProspects) {
                const alternatives = responseWithSelected.prospectAlternatives.filter(
                    (prospect) => prospect.rowNumber === validProspect.rowNumber
                );
                if (alternatives.length === 1) {
                    // If there is only one alternative, select it
                    responseWithSelected.selectedProspects = responseWithSelected.selectedProspects.map((prospect) =>
                        prospect.rowNumber === validProspect.rowNumber ? alternatives[0] : prospect
                    );
                }
            }

            aggregatedResponse.selectedProspects.push(...responseWithSelected.selectedProspects);
            aggregatedResponse.validProspects.push(...responseWithSelected.validProspects);
            aggregatedResponse.alreadyExistingProspects.push(...responseWithSelected.alreadyExistingProspects);
            aggregatedResponse.errorProspects.push(...responseWithSelected.errorProspects);
            aggregatedResponse.prospectAlternatives.push(...responseWithSelected.prospectAlternatives);
            aggregatedResponse.skippedProspects.push(...responseWithSelected.skippedProspects);
        }

        dispatch(updateImportProspectsReviewDetails(aggregatedResponse));
    } catch (error) {
        console.log('error creating bulk import');
    } finally {
        //dispatch(stopLoading());
    }
};

export const runBulkImport =
    (msalInstance: IPublicClientApplication, campaignIdParam?: string) => async (dispatch, getState) => {
        const profilesSdk = getZaplifySdk(apiEndpoint, msalInstance).profiles;
        const campaignId = campaignIdParam || getState().newCampaign.campaignId;
        const prospects = JSON.parse(JSON.stringify(getState().importProspects.reviewDetails.selectedProspects)) as ({
            rowNumber: string;
        } & CreateProspectDataDto)[];
        const originalProspects = getState().importProspects.reviewDetails.validProspects;
        const fieldMapping = getState().importProspects.fieldMapping;
        try {
            const hasIndustryTag = fieldMapping.find((field) => field.prospect === 'industry');
            const hasOrganizationIndustryTag = fieldMapping.find((field) => field.prospect === 'organizationIndustry');

            // Each added prospect need to have organizationIndustry field if industry field is present,
            // so when running a campaign which uses 'industry' tag, will properly send messages,
            if (hasIndustryTag && !hasOrganizationIndustryTag) {
                prospects.forEach((prospect) => {
                    prospect.organizationIndustry = prospect.industry;
                });
            }

            for (const prospect of prospects) {
                prospect.originalData = originalProspects.find(
                    (originalProspect) => originalProspect.rowNumber === prospect.rowNumber
                );
            }

            const importedProspectIds = [];
            for (const batchOfProspects of chunkArray(prospects, 500)) {
                const importedIds = await profilesSdk.sources.runBulkImport(batchOfProspects, campaignId);
                importedProspectIds.push(...importedIds);
            }

            if (importedProspectIds?.length === 0) {
                console.log('imported prospects length === 0 ', prospects, importedProspectIds);
                return false;
            }

            return true;
        } catch (error) {
            console.log('error creating bulk import');
        } finally {
            //dispatch(stopLoading());
        }
    };
